import { P2GButton, P2GMenuItem, P2GMenuList } from '@parcel2go/core-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  menu: {
    position: 'absolute !important' as 'absolute',
    width: 'auto',
    minWidth: '200px'
  }
}));

type EditParcelButtonProps = {
  index: number;
  name: string;
  options: string[];
  onEdit: (id: string, action: string) => void;
  onRemove?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  setOpenSwitcher: (value: boolean) => void;
  fullWidth?: boolean;
};

export const EditParcelButton = ({
  index,
  name,
  options,
  onEdit,
  setOpenSwitcher,
  onRemove,
  onDuplicate,
  fullWidth = false
}: EditParcelButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { classes } = useStyles();

  const doWorkflowAction = useCallback(
    (action: string) => {
      setAnchorEl(null);
      onEdit(name, action);
    },
    [onEdit, setAnchorEl, name]
  );

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <>
      <P2GButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        variant="tertiary"
        dataTestId={`basket-item-header-edit-${index}`}
        fullWidth={fullWidth}
      >
        {t('BASKET_SUMMARY_HEADER_EDIT_MENU_BUTTON', { defaultValue: 'Edit Parcel' })}
      </P2GButton>
      <P2GMenuList open={open} onClose={() => setAnchorEl(null)} anchorRef={anchorEl} classNames={classes.menu}>
        {options.includes('Parcels') && (
          <P2GMenuItem onClick={() => doWorkflowAction('Parcels')}>
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_EDIT_PARCELS', { defaultValue: 'Edit Parcel Details' })}
          </P2GMenuItem>
        )}
        {options.includes('Origin') && (
          <P2GMenuItem onClick={() => doWorkflowAction('Origin')}>
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_EDIT_ADDRESS', { defaultValue: 'Edit Address Details' })}
          </P2GMenuItem>
        )}
        {options.includes('CollectionDate') && (
          <P2GMenuItem onClick={() => doWorkflowAction('CollectionDate')}>
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_EDIT_COLLECTION_DATE', { defaultValue: 'Edit Collection Date' })}
          </P2GMenuItem>
        )}
        {options.includes('DropLocations') && (
          <P2GMenuItem onClick={() => doWorkflowAction('DropLocations')}>
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_EDIT_DROPSHOPS', { defaultValue: 'View Drop Locations' })}
          </P2GMenuItem>
        )}
        {options.includes('DestinationPickUpLocations') && (
          <P2GMenuItem onClick={() => doWorkflowAction('DestinationPickUpLocations')}>
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_EDIT_COLLECTSHOPS', { defaultValue: 'View Collection Locations' })}
          </P2GMenuItem>
        )}
        {options.includes('SwitchService') && (
          <P2GMenuItem
            onClick={() => {
              setAnchorEl(null);
              setOpenSwitcher(true);
            }}
          >
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_CHANGE_SERVICE', { defaultValue: 'Change the Service' })}
          </P2GMenuItem>
        )}
        {onRemove && (
          <P2GMenuItem
            onClick={() => {
              setAnchorEl(null);
              onRemove(name);
            }}
          >
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_DELETE', { defaultValue: 'Delete this Parcel' })}
          </P2GMenuItem>
        )}
        {onDuplicate && (
          <P2GMenuItem
            onClick={() => {
              setAnchorEl(null);
              onDuplicate(name);
            }}
          >
            {t('BASKET_SUMMARY_HEADER_EDIT_MENU_DUPLICATE', { defaultValue: 'Duplicate this Parcel' })}
          </P2GMenuItem>
        )}
      </P2GMenuList>
    </>
  );
};
